import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import MaterialTable from "@material-table/core";

import { useSelector, useDispatch } from "react-redux";
import {
  MinimalStoreInventoryItem,
  StoreInventoryItem
} from "../../models/StoreInventory";
import { v4 as uuidv4 } from "uuid";

import {
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  Radio,
  RadioGroup
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { thunkFetchAllStoreInventoryItems } from "../../store/stores/actions";
import {
  ProductCombinationOffer,
  CampaignDiscountType,
  CampaignProduct,
  CampaignTargetUserType
} from "../../models/Campaign";
import { ImageEditor } from "../../components/editors/ImageEditor";
import { AppState } from "../../store";
import { AllInventoryItemsState } from "../../store/stores/types";
import { ProductState } from "../../store/products/types";
import { Product } from "../../models/Product";
import { Currency } from "../../components/misc/Currency";
import { Alert } from "@material-ui/lab";
import { ComboCampaignNoCombinationsSelect } from "../../components/selects/ComboCampaignNoCombinationsSelect";
import { UserContext } from "../../UserProvider";
import { Authenticated } from "../../models/User";
import { useI18n } from "../../hooks/useI18n";

interface Props {
  storeIds: string[];
  campaignTargetUserType: CampaignTargetUserType;
  campaignDiscountType: CampaignDiscountType;
  dialogOpened: boolean;
  excludedProductIds?: string[];
  existingProductCombination?: ProductCombinationOffer;
  onSave: (combinationProductCampaign: ProductCombinationOffer) => void;
}

export const CombinationProductSelector: FunctionComponent<Props> = ({
  storeIds,
  campaignTargetUserType,
  campaignDiscountType,
  excludedProductIds,
  existingProductCombination,
  dialogOpened,
  onSave
}) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const [] = useState<boolean>(dialogOpened);

  const [combinationProduct, setCombinationProduct] =
    useState<ProductCombinationOffer>(
      existingProductCombination || {
        id: uuidv4(),
        numberOfCombinations: 2,
        swedishTitleTranslation: "",
        swedishDescriptionTranslation: "",
        englishTitleTranslation: "",
        englishDescriptionTranslation: "",
        image: { url: "" },
        products: [],
        inCombinationWith: []
      }
    );

  const productState = useSelector<AppState, ProductState>(
    s => s.storeClusterProducts.productState
  );

  const { products, isLoading } = productState;

  const saveButtonDisabled = () => {
    if (
      campaignDiscountType === CampaignDiscountType.FixedPrice &&
      !combinationProduct?.price
    ) {
      return true;
    }

    if (combinationProduct.products.length === 0) {
      return true;
    }

    return false;
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: 20
        }}
      >
        <Typography variant="h6" gutterBottom component="div">
          {t("common.productCombinations")}
        </Typography>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 30
        }}
      >
        <Typography
          variant="body1"
          style={{ marginTop: "0.6rem", color: theme.palette.text.secondary }}
          gutterBottom
        >
          {t("common.campaigns.selectTheProductsYouWantToBePartOfTheCampaign")}
        </Typography>
      </div>
      <div>
        <TextField
          label={t("common.formFields.campaignPrice")}
          required
          error={
            campaignDiscountType === CampaignDiscountType.FixedPrice &&
            !combinationProduct.price
          }
          disabled={campaignDiscountType === CampaignDiscountType.Percentage}
          style={{ width: 300 }}
          type="number"
          value={combinationProduct?.price}
          onChange={event => {
            setCombinationProduct({
              ...combinationProduct,
              price: parseFloat(event.target.value)
            });
          }}
        />
        {campaignDiscountType === CampaignDiscountType.Percentage && (
          <Alert severity="warning" style={{ marginTop: 5, marginBottom: 10 }}>
            {t("common.messages.campaignDiscountTypePercentageProductWarning")}
          </Alert>
        )}
      </div>
      <div>
        <TextField
          inputProps={{
            maxLength: 35
          }}
          id="standard-basic"
          type="number"
          label={t("common.formFields.minimumPurchaseAmount")}
          value={combinationProduct?.minimumPurchaseAmount}
          style={{ width: 300 }}
          onChange={event => {
            setCombinationProduct({
              ...combinationProduct,
              minimumPurchaseAmount: parseInt(event.target.value)
            });
          }}
        />
      </div>
      <div>
        <TextField
          inputProps={{
            maxLength: 35
          }}
          id="standard-basic"
          type="number"
          label={t("common.formFields.maximumPurchaseQuantity")}
          style={{ width: 300 }}
          value={combinationProduct?.maximumPurchaseQuantity}
          onChange={event => {
            setCombinationProduct({
              ...combinationProduct,
              maximumPurchaseQuantity: parseInt(event.target.value)
            });
          }}
        />
      </div>
      <div>
        <TextField
          inputProps={{
            maxLength: 35
          }}
          id="standard-basic"
          type="number"
          label={t("common.formFields.maximumPurchaseCount")}
          style={{ width: 300 }}
          value={combinationProduct?.maximumPurchaseCount}
          onChange={event => {
            setCombinationProduct({
              ...combinationProduct,
              maximumPurchaseCount: parseInt(event.target.value)
            });
          }}
        />
      </div>
      {currentUser.tenant?.configuration?.features?.hasMultipleCombos && (
        <div style={{ width: 300, marginTop: "1rem" }}>
          <ComboCampaignNoCombinationsSelect
            value={combinationProduct.numberOfCombinations}
            style={{ width: 300 }}
            onSelected={value => {
              setCombinationProduct({
                ...combinationProduct,
                numberOfCombinations: value
              });
            }}
          />
        </div>
      )}
      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <h3>{t("common.messages.provideContentForComboOffer")}</h3>
        <FormControl>
          <RadioGroup
            row
            defaultValue={combinationProduct.hasContent ? "Yes" : undefined}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setCombinationProduct({
                ...combinationProduct,
                hasContent: event.target.value === "Yes" ? true : false
              });
            }}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio />}
              label={t("common.yes")}
            />
            <FormControlLabel
              value="No"
              control={<Radio />}
              label={t("common.no")}
            />
          </RadioGroup>
        </FormControl>
      </div>
      {combinationProduct && combinationProduct.hasContent && (
        <>
          <div>
            <TextField
              defaultValue={combinationProduct.swedishTitleTranslation}
              inputProps={{
                maxLength: 35
              }}
              id="standard-basic"
              label={t("common.formFields.swedishTitleMax35")}
              style={{ width: 300, marginBottom: 20 }}
              onChange={event => {
                setCombinationProduct({
                  ...combinationProduct,
                  swedishTitleTranslation: event.target.value
                });
              }}
            />
          </div>

          <div>
            <TextField
              defaultValue={combinationProduct.englishTitleTranslation}
              inputProps={{
                maxLength: 35
              }}
              id="standard-basic"
              label={t("common.formFields.englishTitleMax35")}
              style={{ width: 300, marginBottom: 20 }}
              onChange={event => {
                setCombinationProduct({
                  ...combinationProduct,
                  englishTitleTranslation: event.target.value
                });
              }}
            />
          </div>
          <div>
            <ImageEditor
              initial={combinationProduct.image ?? { url: "" }}
              onChange={data =>
                setCombinationProduct({ ...combinationProduct, image: data })
              }
            />
          </div>
        </>
      )}

      <MaterialTable
        title=" "
        columns={[
          {
            title: t("common.messages.ean"),
            field: "gtin",
            hidden: true,
            searchable: true
          },
          {
            title: t("common.messages.product"),
            field: "name",
            render: (row: Product) => {
              return (
                <div style={{ minWidth: 180 }}>
                  <Typography>{row.name}</Typography>
                  <Typography color="textSecondary">
                    {`${row.gtin}, `}
                    {row.details?.size ? `${row.details?.size}` : ""}{" "}
                    {row?.brandName}
                  </Typography>
                </div>
              );
            }
          },
          {
            title: t("common.messages.price"),
            field: "pricing.price",
            render: row => {
              return (
                <div>
                  <Typography>
                    <Currency
                      value={row.pricing.price}
                      priceUnit={row.pricing.priceUnit}
                    />
                  </Typography>
                </div>
              );
            }
          }
        ]}
        isLoading={isLoading}
        data={products.filter(p => !excludedProductIds?.includes(p.id))}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)" }
            },
            tooltip: t("common.actions.addToCombinationList"),
            onClick: (event, rowData: any) => {
              const product = products.find((si: any) => si.id === rowData.id);

              if (!product) {
                return;
              }

              const existingSelectedCampaignProduct =
                combinationProduct.products.filter(
                  (p: any) => p.id === rowData.id
                );

              if (existingSelectedCampaignProduct.length > 0) {
                return;
              }

              setCombinationProduct({
                ...combinationProduct,
                products: [...combinationProduct.products, product]
              });
            }
          }
        ]}
        options={{
          actionsColumnIndex: -1,
          headerStyle: {
            padding: mobile ? "8px" : ""
          }
        }}
      />
      <Table size="small" style={{ marginTop: 20 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t("common.messages.products")}</TableCell>
            <TableCell>{t("common.actions.delete")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {combinationProduct.products.map((product: CampaignProduct) => {
            return (
              <>
                <TableRow>
                  <TableCell>
                    {/* {product.name} */}
                    {products.length > 0 &&
                      products.find(p => p.id == product.id)?.name}
                    {/* {storeInventoryItems.length > 0 &&
                      storeInventoryItems.find(
                        (si: MinimalStoreInventoryItem) =>
                          si.productId === product.id
                      )?.productName} */}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => {
                        setCombinationProduct({
                          ...combinationProduct,
                          products: combinationProduct.products.filter(
                            p => p.id !== product.id
                          )
                        });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </>
            );
          })}
        </TableBody>
      </Table>

      <div style={{ display: "flex", justifyContent: "center", marginTop: 50 }}>
        <Button
          variant="contained"
          color="primary"
          style={{ width: 300 }}
          disabled={saveButtonDisabled()}
          onClick={() => {
            onSave(combinationProduct);
          }}
        >
          {t("common.actions.save")}
        </Button>
      </div>
    </>
  );
};
